// PASO 5.4: FieldSupervisorService.js - Servicio para supervisor de campo
// DÍA 5: Dashboards Especializados por Rol - Field Supervisor Service
// INTEGRACIÓN BACKEND REAL - PASO 1 Y 2

import { BaseRoleService } from './BaseRoleService.js'
import { apiClient } from '@/services/api-client'
import { buildApiUrl, BACKEND_CONFIG, getDefaultHeaders } from '@/config/backend-integration.js'

/**
 * Servicio especializado para Field Supervisor
 * Maneja operaciones de campo, equipos de trabajo, recursos y comunicaciones
 */
export class FieldSupervisorService extends BaseRoleService {
  constructor() {
    super('field_supervisor')
    this.apiClient = apiClient
  }

  // ===============================
  // 🚀 OPERACIONES DE CAMPO - BACKEND REAL
  // ===============================

  /**
   * Obtener operaciones de campo del área asignada - BACKEND REAL
   */
  async getFieldOperations(fieldId) {
    try {
      console.log('🚀 Obteniendo operaciones desde backend real:', fieldId)
      
      const url = buildApiUrl(BACKEND_CONFIG.ENDPOINTS.FIELD_OPERATIONS)
      const response = await apiClient.get(url, {
        headers: getDefaultHeaders(),
        params: fieldId ? { fieldId } : {},
        timeout: BACKEND_CONFIG.TIMEOUTS.API_TIMEOUT
      })
      
      console.log('✅ Operaciones obtenidas desde backend:', response.data.length)
      return this.processOperationsData(response.data)
      
    } catch (error) {
      console.error('❌ Error obteniendo operaciones de campo:', error)
      throw this.handleServiceError(error, 'No se pudieron leer las operaciones de campo')
    }
  }

  /**
   * Iniciar una operación de campo
   */
  async startOperation(operationId) {
    try {
      console.log('🚀 Iniciando operación:', operationId)
      
      const response = await this.apiClient.patch(`/field-supervisor/operations/${operationId}/start`, {
        startedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Operación iniciada exitosamente')
      return response.data
      
    } catch (error) {
      console.error('❌ Error iniciando operación:', error)
      throw this.handleServiceError(error, 'Error iniciando operación')
    }
  }

  /**
   * Pausar una operación de campo
   */
  async pauseOperation(operationId) {
    try {
      console.log('⏸️ Pausando operación:', operationId)
      
      const response = await this.apiClient.patch(`/field-supervisor/operations/${operationId}/pause`, {
        pausedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Operación pausada')
      return response.data
      
    } catch (error) {
      console.error('❌ Error pausando operación:', error)
      throw this.handleServiceError(error, 'Error pausando operación')
    }
  }

  /**
   * Completar una operación de campo
   */
  async completeOperation(operationId) {
    try {
      console.log('✅ Completando operación:', operationId)
      
      const response = await this.apiClient.patch(`/field-supervisor/operations/${operationId}/complete`, {
        completedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Operación completada')
      return response.data
      
    } catch (error) {
      console.error('❌ Error completando operación:', error)
      throw this.handleServiceError(error, 'Error completando operación')
    }
  }

  // ===============================
  // 👥 GESTIÓN DE EQUIPOS DE TRABAJO
  // ===============================

  /**
   * Obtener equipos de trabajo del área
   */
  async getWorkTeams(fieldId) {
    try {
      console.log('👥 Obteniendo equipos de trabajo:', fieldId)
      
      const response = await this.apiClient.get(`/field-supervisor/areas/${fieldId}/teams`, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Equipos obtenidos:', response.data.length)
      return this.processTeamsData(response.data)
      
    } catch (error) {
      console.error('❌ Error obteniendo equipos:', error)
      throw this.handleServiceError(error, 'No se pudieron leer los equipos de trabajo')
    }
  }

  /**
   * Asignar equipo a una ruta
   */
  async assignTeam(teamId, routeId) {
    try {
      console.log('👥 Asignando equipo:', teamId, 'a ruta:', routeId)
      
      const response = await this.apiClient.post('/field-supervisor/team-assignments', {
        teamId,
        routeId,
        assignedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Equipo asignado exitosamente')
      return response.data
      
    } catch (error) {
      console.error('❌ Error asignando equipo:', error)
      throw this.handleServiceError(error, 'Error asignando equipo')
    }
  }

  /**
   * Reasignar equipo a nueva ruta
   */
  async reassignTeam(teamId, newRouteId) {
    try {
      console.log('🔄 Reasignando equipo:', teamId, 'a nueva ruta:', newRouteId)
      
      const response = await this.apiClient.put(`/field-supervisor/teams/${teamId}/reassign`, {
        newRouteId,
        reassignedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Equipo reasignado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error reasignando equipo:', error)
      throw this.handleServiceError(error, 'Error reasignando equipo')
    }
  }

  /**
   * Solicitar apoyo de emergencia
   */
  async requestEmergencySupport(supportType, location) {
    try {
      console.log('🚨 Solicitando apoyo de emergencia:', supportType)
      
      const response = await this.apiClient.post('/field-supervisor/emergency-support', {
        supportType,
        location,
        requestedAt: new Date().toISOString(),
        priority: 'critical'
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Apoyo de emergencia solicitado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error solicitando apoyo:', error)
      throw this.handleServiceError(error, 'Error solicitando apoyo')
    }
  }

  // ===============================
  // 📦 GESTIÓN DE RECURSOS
  // ===============================

  /**
   * Obtener recursos de campo disponibles
   */
  async getFieldResources(fieldId) {
    try {
      console.log('📦 Obteniendo recursos de campo:', fieldId)
      
      const response = await this.apiClient.get(`/field-supervisor/areas/${fieldId}/resources`, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Recursos obtenidos:', response.data.length)
      return this.processResourcesData(response.data)
      
    } catch (error) {
      console.error('❌ Error obteniendo recursos:', error)
      throw this.handleServiceError(error, 'No se pudieron leer los recursos de campo')
    }
  }

  /**
   * Solicitar recurso de campo
   */
  async requestResource(resourceId, quantity) {
    try {
      console.log('📦 Solicitando recurso:', resourceId, 'cantidad:', quantity)
      
      const response = await this.apiClient.post('/field-supervisor/resource-requests', {
        resourceId,
        quantity,
        requestedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Recurso solicitado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error solicitando recurso:', error)
      throw this.handleServiceError(error, 'Error solicitando recurso')
    }
  }

  /**
   * Transferir recurso entre ubicaciones
   */
  async transferResource(resourceId, fromLocation, toLocation) {
    try {
      console.log('🚚 Transfiriendo recurso:', resourceId)
      
      const response = await this.apiClient.post('/field-supervisor/resource-transfers', {
        resourceId,
        fromLocation,
        toLocation,
        transferredAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Recurso transferido')
      return response.data
      
    } catch (error) {
      console.error('❌ Error transfiriendo recurso:', error)
      throw this.handleServiceError(error, 'Error transfiriendo recurso')
    }
  }

  /**
   * Solicitar reabastecimiento de emergencia
   */
  async emergencyRestock(resourceType, urgency) {
    try {
      console.log('🆘 Reabastecimiento de emergencia:', resourceType)
      
      const response = await this.apiClient.post('/field-supervisor/emergency-restock', {
        resourceType,
        urgency,
        requestedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Reabastecimiento solicitado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error en reabastecimiento:', error)
      throw this.handleServiceError(error, 'Error en reabastecimiento')
    }
  }

  // ===============================
  // 💬 COMUNICACIONES Y ALERTAS
  // ===============================

  /**
   * Obtener comunicaciones de campo
   */
  async getFieldCommunications(fieldId) {
    try {
      console.log('💬 Obteniendo comunicaciones:', fieldId)
      
      const response = await this.apiClient.get(`/field-supervisor/areas/${fieldId}/communications`, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Comunicaciones obtenidas:', response.data.length)
      return this.processCommunicationsData(response.data)
      
    } catch (error) {
      console.error('❌ Error obteniendo comunicaciones:', error)
      throw this.handleServiceError(error, 'No se pudieron leer las comunicaciones de campo')
    }
  }

  /**
   * Enviar mensaje de campo
   */
  async sendMessage(message) {
    try {
      console.log('💬 Enviando mensaje de campo')
      
      const response = await this.apiClient.post('/field-supervisor/messages', {
        ...message,
        sentAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Mensaje enviado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error enviando mensaje:', error)
      throw this.handleServiceError(error, 'Error enviando mensaje')
    }
  }

  /**
   * Enviar alerta general
   */
  async broadcastAlert(alert) {
    try {
      console.log('📢 Enviando alerta general')
      
      const response = await this.apiClient.post('/field-supervisor/alerts/broadcast', {
        ...alert,
        broadcastAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Alerta enviada')
      return response.data
      
    } catch (error) {
      console.error('❌ Error enviando alerta:', error)
      throw this.handleServiceError(error, 'Error enviando alerta')
    }
  }

  /**
   * Escalar emergencia
   */
  async escalateEmergency(emergencyId, level) {
    try {
      console.log('⚠️ Escalando emergencia:', emergencyId)
      
      const response = await this.apiClient.post(`/field-supervisor/emergencies/${emergencyId}/escalate`, {
        escalationLevel: level,
        escalatedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Emergencia escalada')
      return response.data
      
    } catch (error) {
      console.error('❌ Error escalando emergencia:', error)
      throw this.handleServiceError(error, 'Error escalando emergencia')
    }
  }

  // ===============================
  // 📊 MÉTRICAS Y REPORTES
  // ===============================

  /**
   * Obtener métricas de campo
   */
  async getFieldMetrics(fieldId) {
    try {
      console.log('📊 Obteniendo métricas de campo:', fieldId)
      
      const response = await this.apiClient.get(`/field-supervisor/areas/${fieldId}/metrics`, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Métricas obtenidas')
      return response.data
      
    } catch (error) {
      console.error('❌ Error obteniendo métricas:', error)
      throw this.handleServiceError(error, 'No se pudieron leer las métricas de campo')
    }
  }

  /**
   * Exportar reporte de campo
   */
  async exportFieldReport(fieldId) {
    try {
      console.log('📊 Exportando reporte de campo:', fieldId)
      
      const response = await this.apiClient.post(`/field-supervisor/areas/${fieldId}/reports/export`, {
        exportedAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Reporte exportado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error exportando reporte:', error)
      throw this.handleServiceError(error, 'Error exportando reporte')
    }
  }

  /**
   * Programar análisis de campo
   */
  async scheduleAnalysis(fieldId) {
    try {
      console.log('📈 Programando análisis:', fieldId)
      
      const response = await this.apiClient.post(`/field-supervisor/areas/${fieldId}/analysis/schedule`, {
        scheduledAt: new Date().toISOString()
      }, {
        headers: this.getAuthHeaders()
      })
      
      console.log('✅ Análisis programado')
      return response.data
      
    } catch (error) {
      console.error('❌ Error programando análisis:', error)
      throw this.handleServiceError(error, 'Error programando análisis')
    }
  }

  // ===============================
  // 🔄 MÉTODOS DE PROCESAMIENTO
  // ===============================

  processOperationsData(operations) {
    return operations.map(op => ({
      ...op,
      statusColor: this.getOperationStatusColor(op.status),
      priorityColor: this.getOperationPriorityColor(op.priority),
      durationFormatted: this.formatDuration(op.estimatedDuration)
    }))
  }

  processTeamsData(teams) {
    return teams.map(team => ({
      ...team,
      statusColor: this.getTeamStatusColor(team.status),
      availabilityLevel: this.getTeamAvailability(team)
    }))
  }

  processResourcesData(resources) {
    return resources.map(resource => ({
      ...resource,
      statusColor: this.getResourceStatusColor(resource.status),
      availabilityPercentage: this.calculateResourceAvailability(resource)
    }))
  }

  processCommunicationsData(communications) {
    return communications.map(comm => ({
      ...comm,
      priorityColor: this.getCommunicationPriorityColor(comm.priority),
      timeAgo: this.calculateTimeAgo(comm.timestamp)
    }))
  }

  // ===============================
  // 🎨 MÉTODOS DE UTILIDAD
  // ===============================

  getOperationStatusColor(status) {
    const colors = {
      planned: 'info',
      active: 'success',
      paused: 'warning',
      completed: 'primary',
      cancelled: 'error'
    }
    return colors[status] || 'grey'
  }

  getOperationPriorityColor(priority) {
    const colors = {
      low: 'success',
      medium: 'info',
      high: 'warning',
      critical: 'error'
    }
    return colors[priority] || 'grey'
  }

  getTeamStatusColor(status) {
    const colors = {
      available: 'success',
      assigned: 'info',
      busy: 'warning',
      off_duty: 'grey'
    }
    return colors[status] || 'grey'
  }

  getResourceStatusColor(status) {
    const colors = {
      available: 'success',
      reserved: 'info',
      depleted: 'warning',
      maintenance: 'error'
    }
    return colors[status] || 'grey'
  }

  getCommunicationPriorityColor(priority) {
    const colors = {
      normal: 'success',
      high: 'warning',
      urgent: 'error'
    }
    return colors[priority] || 'grey'
  }

  formatDuration(minutes) {
    if (!minutes) return 'No estimado'
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`
  }

  getTeamAvailability(team) {
    const availableMembers = team.members.filter(m => m.status === 'active').length
    const totalMembers = team.members.length
    return totalMembers > 0 ? Math.round((availableMembers / totalMembers) * 100) : 0
  }

  calculateResourceAvailability(resource) {
    // Lógica simple de disponibilidad basada en cantidad
    if (resource.quantity <= 0) return 0
    if (resource.quantity >= 100) return 100
    return resource.quantity
  }

  calculateTimeAgo(timestamp) {
    const now = new Date()
    const time = new Date(timestamp)
    const diffMs = now - time
    const diffMins = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMins / 60)
    
    if (diffHours > 0) return `${diffHours}h atrás`
    if (diffMins > 0) return `${diffMins}m atrás`
    return 'Ahora'
  }

  // ===============================
  // ⚠️ ERRORES
  // ===============================

  /**
   * P13-V7B — every write of this service ended in `throw this.handleServiceError(...)`, a method
   * that was defined nowhere (not here, not in BaseRoleService), so a rejected request raised a
   * TypeError instead of the failure it was reporting. It now exists: the server's own message
   * when there is one, the caller's description otherwise, with the status and code preserved.
   *
   * The mock datasets that the readers returned on failure were removed with this repair: a read
   * that failed is reported as a failure, never answered with invented operations, teams,
   * resources, messages or metrics. The actor stamps (`startedBy`, `sentBy`, ...) called a second
   * helper that was equally undefined and are gone too — the server derives the actor from the
   * session, the client does not supply it.
   */
  handleServiceError(error, fallbackMessage) {
    const responseData = error?.response?.data || {}
    const normalized = new Error(responseData.error || responseData.message || error?.message || fallbackMessage)
    normalized.status = error?.response?.status || error?.status
    normalized.code = responseData.code || error?.code
    return normalized
  }
}

// Singleton para uso global
export const fieldSupervisorService = new FieldSupervisorService()
export default FieldSupervisorService
